import React, { useState, useEffect, useMemo } from 'react';
import {
    Layout, Form, Input, Button, Space, message,
    Switch, Tag, Divider, Tooltip, Popconfirm,
    Typography, Alert, Badge, Card
} from 'antd';
import {
    KeyOutlined, SaveOutlined, DeleteOutlined,
    CheckCircleOutlined, SearchOutlined,
    LinkOutlined, ApiOutlined, UserOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Sider, Content } = Layout;
const { Text, Title } = Typography;

// ── 全球 + 中国主流 AI 供应商预设（参考 OpenCowork 数据）──────────────────
const BUILTIN_PROVIDERS = [
    {
        id: 'openai', name: 'OpenAI', icon: '🌐', group: '国际',
        apiBase: 'https://api.openai.com/v1',
        homepage: 'https://openai.com', apiKeyUrl: 'https://platform.openai.com/api-keys',
        description: 'GPT系列，全球领先',
        models: ['gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo', 'o3', 'o4-mini']
    },
    {
        id: 'anthropic', name: 'Anthropic (Claude)', icon: '🧠', group: '国际',
        apiBase: 'https://api.anthropic.com/v1',
        homepage: 'https://anthropic.com', apiKeyUrl: 'https://console.anthropic.com/account/keys',
        description: 'Claude系列，代码和分析出色',
        models: ['claude-opus-4-5', 'claude-sonnet-4-5', 'claude-haiku-4-5', 'claude-3-7-sonnet-latest']
    },
    {
        id: 'google', name: 'Google Gemini', icon: '✨', group: '国际',
        apiBase: 'https://generativelanguage.googleapis.com/v1beta/openai',
        homepage: 'https://ai.google.dev', apiKeyUrl: 'https://aistudio.google.com/app/apikey',
        description: 'Gemini系列，多模态能力强',
        models: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash']
    },
    {
        id: 'deepseek', name: 'DeepSeek', icon: '🔍', group: '中国',
        apiBase: 'https://api.deepseek.com/v1',
        homepage: 'https://platform.deepseek.com', apiKeyUrl: 'https://platform.deepseek.com/api_keys',
        description: '高性价比，代码能力强',
        models: ['deepseek-chat', 'deepseek-reasoner']
    },
    {
        id: 'mirrorstages', name: 'MirrorStages', icon: '🪞', group: '第三方',
        apiBase: 'https://api.mirrorstages.com/openai/v1',
        homepage: 'https://api.mirrorstages.com', apiKeyUrl: 'https://api.mirrorstages.com',
        description: 'GPT-5.3 Codex 高级推理，支持网络访问',
        models: ['gpt-5.3-codex', 'gpt-5.3-codex-spark', 'gpt-5.2-codex', 'gpt-5.1-codex', 'gpt-5-codex', 'gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo']
    },
    {
        id: 'openrouter', name: 'OpenRouter', icon: '🔀', group: '第三方',
        apiBase: 'https://openrouter.ai/api/v1',
        homepage: 'https://openrouter.ai', apiKeyUrl: 'https://openrouter.ai/keys',
        description: '聚合多家厂商，统一 API',
        models: ['openai/gpt-4o', 'anthropic/claude-opus-4-5', 'deepseek/deepseek-chat']
    },
    {
        id: 'ollama', name: 'Ollama (本地)', icon: '🦙', group: '其他',
        apiBase: 'http://localhost:11434/v1',
        homepage: 'https://ollama.com', apiKeyUrl: '',
        description: '本地部署，无需网络，完全私有',
        models: ['llama3.3', 'qwen3:30b', 'deepseek-r1:32b', 'gemma3:27b']
    },
    // ── 中国供应商 ──────────────────────────────────────────────────────
    {
        id: 'zhipu', name: '智谱AI (GLM)', icon: '🤖', group: '中国',
        apiBase: 'https://open.bigmodel.cn/api/paas/v4',
        homepage: 'https://bigmodel.cn', apiKeyUrl: 'https://bigmodel.cn/usercenter/apikeys',
        description: '国产旗舰，支持长文本和多模态',
        models: ['glm-5', 'glm-4.7', 'glm-4.5-air', 'glm-4-plus', 'glm-4-0520', 'glm-4-air', 'glm-4-airx', 'glm-4-flash', 'glm-4v', 'glm-z1-airx']
    },
    {
        id: 'qwen', name: '通义千问 (Qwen)', icon: '💬', group: '中国',
        apiBase: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
        homepage: 'https://dashscope.aliyun.com', apiKeyUrl: 'https://dashscope.console.aliyun.com/apiKey',
        description: '阿里云大模型，稳定企业级',
        models: ['qwen3-max', 'qwen-plus', 'qwen-max', 'qwen-turbo', 'qwen-flash', 'qwen-vl-max']
    },
    {
        id: 'moonshot', name: 'Moonshot / Kimi', icon: '🌙', group: '中国',
        apiBase: 'https://api.moonshot.cn/v1',
        homepage: 'https://platform.moonshot.cn', apiKeyUrl: 'https://platform.moonshot.cn/console/api-keys',
        description: '超长上下文，记忆能力强',
        models: ['kimi-k2.5', 'moonshot-v1-128k', 'moonshot-v1-32k', 'moonshot-v1-8k']
    },
    {
        id: 'minimax', name: 'MiniMax', icon: '⚡', group: '中国',
        apiBase: 'https://api.minimax.chat/v1',
        homepage: 'https://www.minimax.chat', apiKeyUrl: 'https://www.minimax.chat/user-center/basic-information/interface-key',
        description: '国产新锐，性能均衡',
        models: ['MiniMax-M2.5', 'MiniMax-M2.1', 'minimax-m2.5', 'abab6.5s-chat', 'abab6-chat', 'abab5.5-chat']
    },
    {
        id: 'volcengine', name: '字节豆包 (Volcengine)', icon: '🎯', group: '中国',
        apiBase: 'https://ark.cn-beijing.volces.com/api/v3',
        homepage: 'https://www.volcengine.com/product/ark', apiKeyUrl: 'https://console.volcengine.com/ark/region:ark+cn-beijing/apiKey',
        description: '字节跳动旗下，高并发低延迟',
        models: ['doubao-pro-32k', 'doubao-lite-32k', 'doubao-1-5-pro-256k']
    },
    {
        id: 'baidu', name: '百度文心 (ERNIE)', icon: '🔵', group: '中国',
        apiBase: 'https://qianfan.baidubce.com/v2',
        homepage: 'https://cloud.baidu.com/product/wenxinworkshop', apiKeyUrl: 'https://console.bce.baidu.com/iam/#/iam/accesslist',
        description: '百度文心大模型，中文理解优秀',
        models: ['ernie-4.5-turbo-128k', 'ernie-3.5-8k', 'ernie-speed-128k']
    },
    {
        id: 'siliconflow', name: '硅基流动', icon: '💎', group: '中国',
        apiBase: 'https://api.siliconflow.cn/v1',
        homepage: 'https://siliconflow.cn', apiKeyUrl: 'https://cloud.siliconflow.cn/account/ak',
        description: '聚合国内外主流模型，价格优惠',
        models: ['deepseek-ai/DeepSeek-V3.2', 'Qwen/Qwen3-235B-A22B', 'moonshotai/Kimi-K2.5']
    },
    {
        id: 'stepfun', name: '阶跃星辰', icon: '🌟', group: '中国',
        apiBase: 'https://api.stepfun.com/v1',
        homepage: 'https://platform.stepfun.com', apiKeyUrl: 'https://platform.stepfun.com/account-info',
        description: '阶跃星辰大模型，推理能力强',
        models: ['step-3-mini', 'step-2-16k', 'step-1-8k']
    },
    {
        id: 'gitee-ai', name: 'Gitee AI', icon: '🦊', group: '中国',
        apiBase: 'https://ai.gitee.com/v1',
        homepage: 'https://ai.gitee.com', apiKeyUrl: 'https://ai.gitee.com/dashboard/settings/tokens',
        description: '国产开源模型托管平台',
        models: ['Qwen3-235B-A22B', 'DeepSeek-V3', 'GLM-4-32B']
    },
    {
        id: 'baichuan', name: '百川智能', icon: '🏔️', group: '中国',
        apiBase: 'https://api.baichuan-ai.com/v1',
        homepage: 'https://platform.baichuan-ai.com', apiKeyUrl: 'https://platform.baichuan-ai.com/console/apikey',
        description: '百川大模型，知识问答专长',
        models: ['Baichuan4-Turbo', 'Baichuan3-Turbo-128k']
    },
    {
        id: 'custom_template', name: '+ 添加自定义类型', icon: '➕', group: '其他',
        apiBase: '',
        homepage: '', apiKeyUrl: '',
        description: '创建全新的自定义 AI 兼容接口配置',
        models: []
    },
];

const AIProviders = () => {
    const { user } = useAuth();
    const isAdmin = user && (user.role === 'super_admin' || user.role === 'school_admin');

    const [personalForm] = Form.useForm();
    const [globalForm] = Form.useForm();

    const [selectedId, setSelectedId] = useState('zhipu');
    const [searchQuery, setSearchQuery] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // 个人配置列表
    const [personalConfigs, setPersonalConfigs] = useState([]);
    // 全局配置列表（管理员用）
    const [globalConfigs, setGlobalConfigs] = useState([]);
    const [customModelsStr, setCustomModelsStr] = useState('');

    useEffect(() => {
        fetchPersonalConfigs();
        if (isAdmin) fetchGlobalConfigs();
    }, []);

    const fetchPersonalConfigs = async () => {
        try {
            const res = await api.get('/user-ai-auth/me');
            if (res.data.success) setPersonalConfigs(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchGlobalConfigs = async () => {
        try {
            const res = await api.get('/ai-providers');
            if (res.data.success) setGlobalConfigs(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const dynamicProviders = useMemo(() => {
        const customProviders = personalConfigs
            .filter(c => c.provider_name === 'custom' || c.provider_name.startsWith('custom_'))
            .map(c => ({
                id: c.provider_name,
                name: c.custom_name || (c.provider_name === 'custom' ? '自定义供应商（旧配置）' : '自定义配置'),
                icon: '⚙️',
                group: '其他',
                apiBase: '',
                homepage: '',
                apiKeyUrl: '',
                description: '兼容 OpenAI API 的自定义配置',
                models: c.models || []
            }));
        return [...BUILTIN_PROVIDERS, ...customProviders];
    }, [personalConfigs]);

    const selectedPreset = useMemo(
        () => dynamicProviders.find(p => p.id === selectedId),
        [selectedId, dynamicProviders]
    );

    // 个人配置记录
    const personalRecord = useMemo(
        () => personalConfigs.find(p => p.provider_name === selectedId),
        [selectedId, personalConfigs]
    );

    // 全局配置记录
    const globalRecord = useMemo(
        () => globalConfigs.find(p => p.provider_name === selectedId),
        [selectedId, globalConfigs]
    );

    // 切换供应商时填充表单
    useEffect(() => {
        if (!selectedPreset) return;
        personalForm.setFieldsValue({
            custom_name: personalRecord?.custom_name || (selectedId.startsWith('custom') && selectedId !== 'custom_template' ? selectedPreset.name : ''),
            api_key: '',
            api_base: personalRecord?.api_base || selectedPreset.apiBase,
            is_active: personalRecord?.is_active !== false,
        });
        setCustomModelsStr(
            personalRecord?.models?.length
                ? JSON.stringify(personalRecord.models)
                : JSON.stringify(selectedPreset.models)
        );
        if (isAdmin) {
            globalForm.setFieldsValue({
                display_name: globalRecord?.display_name || selectedPreset.name,
                api_key: globalRecord?.api_key && globalRecord.api_key !== '***' ? globalRecord.api_key : '',
                api_base: globalRecord?.api_base || selectedPreset.apiBase,
                is_active: !!globalRecord?.is_active,
            });
        }
    }, [selectedId, personalConfigs, globalConfigs]);

    // 保存个人配置
    const handleSavePersonal = async (values) => {
        let models = selectedPreset.models;
        if (customModelsStr.trim()) {
            try {
                const parsed = JSON.parse(customModelsStr);
                if (Array.isArray(parsed)) models = parsed;
            } catch { message.error('模型列表格式错误'); return; }
        }

        let theProviderName = selectedId;
        if (selectedId === 'custom_template') {
            theProviderName = `custom_${Date.now()}`;
        }

        setSaving(true);
        try {
            await api.post('/user-ai-auth/me', {
                provider_name: theProviderName,
                custom_name: values.custom_name || '',
                api_key: values.api_key || '',
                api_base: values.api_base || selectedPreset.apiBase,
                models,
                is_active: values.is_active !== false,
            });
            message.success('个人配置保存成功！如果创建了新的自定义供应商，可通过左侧菜单重新选中它。');
            fetchPersonalConfigs();
            if (selectedId === 'custom_template') {
                // We keep selectedId as 'custom' so they can create another one if they want,
                // or we could switch to the new one. Switching to the new one is better UX:
                setSelectedId(theProviderName);
            }
        } catch (e) {
            message.error(e.response?.data?.message || '保存失败');
        } finally { setSaving(false); }
    };

    // 删除个人配置
    const handleDeletePersonal = async () => {
        setDeleting(true);
        try {
            await api.delete(`/user-ai-auth/me/${selectedId}`);
            message.success('已删除，将使用学校公共路线');
            fetchPersonalConfigs();
            if (selectedId === 'custom' || selectedId.startsWith('custom_')) {
                setSelectedId('custom_template');
            }
        } catch { message.error('删除失败'); }
        finally { setDeleting(false); }
    };

    const handleTogglePersonal = async (providerName, enabled) => {
        try {
            await api.patch(`/user-ai-auth/me/${providerName}/status`, {
                is_active: enabled
            });
            message.success(enabled ? '已启用该个人模型' : '已停用该个人模型');
            fetchPersonalConfigs();
        } catch (e) {
            message.error(e.response?.data?.message || '状态更新失败');
        }
    };

    // 保存全局配置（管理员）
    const handleSaveGlobal = async (values) => {
        if (selectedId === 'custom_template') {
            message.error('这是一个新建模板，不能设置全局配置');
            return;
        }
        let models = selectedPreset.models;
        if (customModelsStr.trim()) {
            try {
                const parsed = JSON.parse(customModelsStr);
                if (Array.isArray(parsed)) models = parsed;
            } catch { message.error('模型列表格式错误'); return; }
        }
        setSaving(true);
        try {
            const payload = {
                provider_name: selectedId,
                display_name: values.display_name,
                api_key: values.api_key || '',
                api_base: values.api_base || selectedPreset.apiBase,
                models,
                is_active: values.is_active ? 1 : 0,
            };
            if (globalRecord) {
                await api.put(`/ai-providers/${globalRecord.id}`, payload);
            } else {
                await api.post('/ai-providers', payload);
            }
            message.success('全局配置保存成功');
            fetchGlobalConfigs();
        } catch (e) {
            message.error(e.response?.data?.message || '保存失败');
        } finally { setSaving(false); }
    };

    const filteredProviders = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return dynamicProviders.filter(p =>
            !q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)
        );
    }, [searchQuery, dynamicProviders]);

    const groups = ['国际', '中国', '第三方', '其他'];

    return (
        <Layout style={{ height: '100%', background: 'transparent' }}>
            {/* ── 左侧供应商列表 ── */}
            <Sider width={210} style={{
                background: '#fff',
                borderRight: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <div style={{ padding: '10px 10px 6px', borderBottom: '1px solid #f0f0f0' }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />}
                        placeholder="搜索供应商..."
                        size="small"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        allowClear
                    />
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
                    {groups.map(group => {
                        const items = filteredProviders.filter(p => p.group === group);
                        if (!items.length) return null;
                        return (
                            <div key={group}>
                                <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                    {group}
                                </div>
                                {items.map(p => {
                                    const isTemplate = p.id === 'custom_template';
                                    const hasPersonal = !isTemplate && personalConfigs.some(c => c.provider_name === p.id);
                                    const personalItem = !isTemplate ? personalConfigs.find(c => c.provider_name === p.id) : null;
                                    const hasGlobal = !isTemplate && globalConfigs.some(c => c.provider_name === p.id && c.is_active);
                                    const isSelected = selectedId === p.id;
                                    return (
                                        <div
                                            key={p.id}
                                            onClick={() => setSelectedId(p.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 7,
                                                padding: '6px 10px', cursor: 'pointer', borderRadius: 6,
                                                margin: '1px 5px',
                                                background: isSelected ? '#e6f4ff' : 'transparent',
                                                color: isSelected ? '#1677ff' : '#333',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <span style={{ fontSize: 15 }}>{p.icon}</span>
                                            <span style={{ flex: 1, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {p.name}
                                            </span>
                                            <Space size={2}>
                                                {hasPersonal && (
                                                    <Tooltip title="已配置个人 Key">
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: personalItem?.is_active === false ? '#bfbfbf' : '#52c41a', display: 'inline-block' }} />
                                                    </Tooltip>
                                                )}
                                                {hasGlobal && !hasPersonal && (
                                                    <Tooltip title="学校已配置公共 Key">
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1677ff', display: 'inline-block' }} />
                                                    </Tooltip>
                                                )}
                                            </Space>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* 图例 */}
                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', fontSize: 10, color: '#aaa' }}>
                    <Space direction="vertical" size={2}>
                        <Space size={4}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />个人已配置</Space>
                        <Space size={4}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1677ff', display: 'inline-block' }} />学校已配置</Space>
                    </Space>
                </div>
            </Sider>

            {/* ── 右侧配置面板 ── */}
            <Content style={{ padding: '20px 24px', overflowY: 'auto', background: '#fafafa' }}>
                {selectedPreset && (
                    <div style={{ maxWidth: 680 }}>
                        {/* 供应商标题 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <span style={{ fontSize: 32 }}>{selectedPreset.icon}</span>
                            <div style={{ flex: 1 }}>
                                <Title level={4} style={{ margin: 0 }}>{selectedPreset.name}</Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>{selectedPreset.description}</Text>
                            </div>
                            <Space>
                                {selectedPreset.apiKeyUrl && (
                                    <Button size="small" icon={<KeyOutlined />} href={selectedPreset.apiKeyUrl} target="_blank">
                                        获取 Key
                                    </Button>
                                )}
                                {selectedPreset.homepage && (
                                    <Button size="small" icon={<LinkOutlined />} href={selectedPreset.homepage} target="_blank" />
                                )}
                            </Space>
                        </div>

                        {/* ── 个人配置区域（所有教师） ── */}
                        <Card
                            title={
                                <Space>
                                    <UserOutlined style={{ color: '#52c41a' }} />
                                    <span>我的个人配置</span>
                                    {personalRecord && <Badge status="success" text="已配置" />}
                                </Space>
                            }
                            extra={
                                personalRecord && (
                                    <Popconfirm
                                        title="删除后将使用学校公共 Key"
                                        onConfirm={handleDeletePersonal}
                                        okText="删除" okType="danger" cancelText="取消"
                                    >
                                        <Button size="small" danger icon={<DeleteOutlined />} loading={deleting}>删除个人配置</Button>
                                    </Popconfirm>
                                )
                            }
                            style={{ marginBottom: 16 }}
                        >
                            <Alert
                                message="个人 Key 优先级高于学校公共 Key，使用自己的额度，不受学校限速影响"
                                type="success" showIcon
                                style={{ marginBottom: 16, fontSize: 12 }}
                            />
                            {personalRecord && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '10px 12px',
                                        marginBottom: 16,
                                        border: '1px solid #f0f0f0',
                                        borderRadius: 8,
                                        background: '#fafafa',
                                    }}
                                >
                                    <Space size={8}>
                                        <Badge
                                            status={personalRecord.is_active !== false ? 'success' : 'default'}
                                            text={personalRecord.is_active !== false ? '当前状态：已启用' : '当前状态：已停用'}
                                        />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            已保存的个人模型可随时暂停，不必删除 API Key。
                                        </Text>
                                    </Space>
                                    <Switch
                                        checked={personalRecord.is_active !== false}
                                        checkedChildren="启用"
                                        unCheckedChildren="停用"
                                        onChange={(checked) => handleTogglePersonal(personalRecord.provider_name, checked)}
                                    />
                                </div>
                            )}
                            <Form form={personalForm} layout="vertical" onFinish={handleSavePersonal}>
                                {selectedId.startsWith('custom') && (
                                    <Form.Item
                                        label={
                                            <Space>
                                                <span>自定义名称</span>
                                            </Space>
                                        }
                                        name="custom_name"
                                        rules={[{ required: true, message: '请输入自定义名称' }]}
                                    >
                                        <Input size="large" placeholder="例如：我的 Ollama 接口、张三的代理" />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    label={
                                        <Space>
                                            <span>我的 API Key</span>
                                            {personalRecord && <Tag color="green" icon={<CheckCircleOutlined />}>已配置（输入新值可覆盖）</Tag>}
                                        </Space>
                                    }
                                    name="api_key"
                                    rules={[{ required: !personalRecord, message: '请输入 API Key' }]}
                                >
                                    <Input.Password
                                        size="large"
                                        placeholder={personalRecord ? '已配置，输入新 Key 可覆盖' : 'sk-... 或 ms_... 等'}
                                        prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />}
                                    />
                                </Form.Item>

                                <Form.Item label="API Base URL（可选，自动填充）" name="api_base">
                                    <Input size="large" prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />} />
                                </Form.Item>

                                <Form.Item
                                    label="个人模型状态"
                                    name="is_active"
                                    valuePropName="checked"
                                    initialValue={true}
                                >
                                    <Switch checkedChildren="启用" unCheckedChildren="停用" />
                                </Form.Item>

                                {/* 预设模型 */}
                                {selectedPreset.models.length > 0 && (
                                    <Form.Item label={<span>可用模型 <Text type="secondary" style={{ fontSize: 11 }}>（点击标签快速添加）</Text></span>}>
                                        <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {selectedPreset.models.map(m => (
                                                <Tag key={m} style={{ cursor: 'pointer', fontSize: 11 }}
                                                    onClick={() => {
                                                        try {
                                                            const arr = JSON.parse(customModelsStr || '[]');
                                                            if (!arr.includes(m)) setCustomModelsStr(JSON.stringify([...arr, m]));
                                                        } catch { setCustomModelsStr(JSON.stringify([m])); }
                                                    }}>+ {m}</Tag>
                                            ))}
                                        </div>
                                        <Input.TextArea
                                            rows={2} value={customModelsStr}
                                            onChange={e => setCustomModelsStr(e.target.value)}
                                            placeholder='["model-1", "model-2"]'
                                            style={{ fontFamily: 'monospace', fontSize: 11 }}
                                        />
                                    </Form.Item>
                                )}

                                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                    <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />} size="large">
                                        保存个人配置
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>

                        {/* 学校公共配置状态（只读展示） */}
                        {selectedId !== 'custom_template' && (
                        <Card
                            title={
                                <Space>
                                    <GlobalOutlined style={{ color: '#1677ff' }} />
                                    <span>学校公共配置</span>
                                    {globalRecord?.is_active
                                        ? <Badge status="processing" text="已启用" />
                                        : <Badge status="default" text="未配置" />
                                    }
                                </Space>
                            }
                            size="small"
                            style={{ background: '#f9f9f9' }}
                            bodyStyle={{ padding: '12px 16px' }}
                        >
                            {globalRecord?.is_active ? (
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text style={{ fontSize: 12 }}>
                                        <KeyOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                                        学校已配置公共 Key（{globalRecord.api_key === '***' ? '隐藏' : '已设置'}）
                                    </Text>
                                    <Text style={{ fontSize: 12 }}>
                                        <LinkOutlined style={{ marginRight: 4 }} />
                                        {globalRecord.api_base}
                                    </Text>
                                    {!personalRecord && (
                                        <Alert message="您当前使用学校公共路线，配置个人 Key 可获得独立额度" type="info" showIcon style={{ marginTop: 6, fontSize: 11 }} />
                                    )}
                                </Space>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    学校未配置此供应商的公共 Key，请配置个人 Key 使用。
                                </Text>
                            )}

                            {/* 管理员额外显示全局配置表单 */}
                            {isAdmin && (
                                <>
                                    <Divider style={{ margin: '12px 0' }}>管理员全局配置</Divider>
                                    <Form form={globalForm} layout="vertical" onFinish={handleSaveGlobal}>
                                        <Form.Item label="显示名称" name="display_name">
                                            <Input size="small" />
                                        </Form.Item>
                                        <Form.Item label="全局 API Key（所有教师公用）" name="api_key">
                                            <Input.Password size="small" placeholder="全校共享的 API Key" prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />} />
                                        </Form.Item>
                                        <Form.Item label="全局 API Base URL" name="api_base">
                                            <Input size="small" />
                                        </Form.Item>
                                        <Form.Item label="启用" name="is_active" valuePropName="checked">
                                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                                        </Form.Item>
                                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                            <Button type="primary" htmlType="submit" loading={saving} size="small" icon={<SaveOutlined />}>
                                                保存全局配置
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </>
                            )}
                        </Card>
                        )}
                    </div>
                )}
            </Content>
        </Layout>
    );
};

export default AIProviders;
